// useSpeechToText.js — Web Speech API wrapper com melhorias.
//
// Sprint v9.5 — adiciona voz aos composers. Suporta:
//   • pt-BR como default (auto-detect via navigator.language)
//   • Streaming (interim results pra preview live em ghost text)
//   • Continuous mode (não para após pausas curtas — usa restart em onend)
//   • Auto-punctuation heurística: pausa longa entre interim chunks vira "."
//   • Smart capitalization (primeira letra + após "." vira maiúscula)
//   • Cleanup garantido em unmount, error, abort
//   • Fallback: retorna `supported: false` quando o browser não tem
//     SpeechRecognition (alguns Firefox / older Android)
//
// API:
//   const {
//     transcript,        // texto FINAL acumulado (committed)
//     interimTranscript, // texto provisório (atualizando em tempo real)
//     listening,         // bool — está ativo?
//     supported,         // bool — browser suporta?
//     error,             // null | 'not-allowed' | 'network' | 'no-speech'…
//     start, stop, abort, reset,
//   } = useSpeechToText({ lang, continuous, autoStop });

import { useCallback, useEffect, useRef, useState } from 'react';

function getSpeechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function detectDefaultLang() {
  if (typeof navigator === 'undefined') return 'pt-BR';
  const lang = navigator.language || navigator.userLanguage || 'pt-BR';
  // Normaliza: se vier "pt", "pt-PT", "pt_BR", vira "pt-BR" como default
  if (/^pt/i.test(lang)) return 'pt-BR';
  if (/^en/i.test(lang)) return 'en-US';
  if (/^es/i.test(lang)) return 'es-ES';
  return lang;
}

// Capitaliza primeira letra de cada sentença + após ponto.
function smartCapitalize(s) {
  if (!s) return s;
  return s
    .replace(/(^|[.!?]\s+)([a-záéíóúâêôãõç])/g, (_m, prefix, ch) => prefix + ch.toUpperCase());
}

// Adiciona "." no final se a frase parece terminada (sem pontuação final).
// Heurística simples — só quando a transcript final é longa o suficiente.
function autoPunctuate(s) {
  const trimmed = s.trim();
  if (!trimmed || trimmed.length < 4) return s;
  if (/[.!?…]$/.test(trimmed)) return s;
  return `${trimmed}. `;
}

export function useSpeechToText({
  lang,
  continuous = true,
  autoStop = false,
  smartFormatting = true,
} = {}) {
  const SR = getSpeechRecognition();
  const supported = !!SR;

  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const restartingRef = useRef(false);
  const lastFinalAtRef = useRef(0);
  const langRef = useRef(lang || detectDefaultLang());

  // Pra evitar stale closure sobre `continuous` no callback de restart
  const continuousRef = useRef(continuous);
  useEffect(() => {
    continuousRef.current = continuous;
  }, [continuous]);

  const initRecognition = useCallback(() => {
    if (!SR) return null;
    const rec = new SR();
    rec.lang = langRef.current;
    rec.continuous = true; // sempre continuous na engine — gerenciamos restart aqui
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        if (result.isFinal) {
          finalText += text;
        } else {
          interim += text;
        }
      }
      if (finalText) {
        const now = Date.now();
        const elapsed = now - lastFinalAtRef.current;
        lastFinalAtRef.current = now;
        setTranscript((prev) => {
          let next = finalText;
          if (smartFormatting) {
            // Pausa longa (> 2.5s) entre chunks finais → adiciona "." antes
            if (prev && elapsed > 2500 && !/[.!?…]\s*$/.test(prev)) {
              return autoPunctuate(prev) + smartCapitalize(next).trim() + ' ';
            }
            return prev + smartCapitalize(next) + ' ';
          }
          return prev + (prev ? ' ' : '') + next;
        });
        setInterimTranscript('');
      } else {
        setInterimTranscript(smartFormatting ? smartCapitalize(interim) : interim);
      }
    };

    rec.onerror = (event) => {
      // not-allowed, no-speech, network, audio-capture, aborted
      if (event.error === 'no-speech') {
        // não é um erro fatal — só silêncio. continuous mode reinicia.
        return;
      }
      if (event.error === 'aborted') {
        return;
      }
      setError(event.error || 'unknown');
      setListening(false);
      restartingRef.current = false;
    };

    rec.onend = () => {
      // Se continuous está ativo e não foi stop manual, reinicia
      if (restartingRef.current && continuousRef.current) {
        try {
          rec.start();
        } catch {
          restartingRef.current = false;
          setListening(false);
        }
      } else {
        setListening(false);
      }
    };

    rec.onstart = () => {
      setError(null);
      setListening(true);
    };

    return rec;
  }, [SR, smartFormatting]);

  const start = useCallback(() => {
    if (!supported) {
      setError('not-supported');
      return;
    }
    if (listening) return;
    const rec = initRecognition();
    if (!rec) return;
    recognitionRef.current = rec;
    restartingRef.current = true;
    lastFinalAtRef.current = Date.now();
    try {
      rec.start();
    } catch (e) {
      setError(e?.name || 'start-failed');
      restartingRef.current = false;
    }
  }, [supported, listening, initRecognition]);

  const stop = useCallback(() => {
    restartingRef.current = false;
    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      rec.stop();
    } catch {
      // ignore
    }
  }, []);

  const abort = useCallback(() => {
    restartingRef.current = false;
    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      rec.abort();
    } catch {
      // ignore
    }
    setInterimTranscript('');
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, []);

  // Auto-stop ao desmontar
  useEffect(() => () => abort(), [abort]);

  // Se `lang` mudar entre renders, atualiza o ref
  useEffect(() => {
    if (lang) langRef.current = lang;
  }, [lang]);

  // autoStop: se true, para após primeiro final result
  useEffect(() => {
    if (!autoStop) return;
    if (transcript.trim().length > 0) {
      stop();
    }
  }, [transcript, autoStop, stop]);

  return {
    transcript,
    interimTranscript,
    listening,
    supported,
    error,
    start,
    stop,
    abort,
    reset,
  };
}
